<script setup>
import { computed, getCurrentInstance, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { DriverStore } from '../pinia/Driver.js'
import { notifyDialog } from '../composables/useDialogNotice'

const { proxy } = getCurrentInstance()
const route = useRoute()
const router = useRouter()
const Driver = DriverStore()
const open = ref(false)
const state = ref('form')
const copy = ref('Enter a new password for your account.')
const password = ref('')
const confirm = ref('')
let token = ''
let inFlight = false

const title = computed(() =>
  state.value === 'saving'
    ? 'Updating password'
    : state.value === 'success'
      ? 'Password updated'
      : state.value === 'invalid'
        ? 'Link not valid'
        : state.value === 'error'
          ? 'Reset failed'
          : 'Reset password'
)
const icon = computed(() =>
  state.value === 'success'
    ? 'mdi-check-circle'
    : state.value === 'saving'
      ? null
      : state.value === 'form'
        ? 'mdi-lock'
        : 'mdi-alert-circle'
)
const iconClass = computed(() =>
  state.value === 'success'
    ? 'success'
    : state.value === 'invalid' || state.value === 'error'
      ? 'error'
      : state.value === 'saving'
        ? 'saving'
        : 'form'
)
const checks = computed(() => {
  const value = password.value || ''
  return [
    { id: 'length', label: 'At least 8 characters', ok: value.length >= 8 },
    { id: 'upper', label: 'At least 1 uppercase letter', ok: /[A-Z]/.test(value) },
    { id: 'number', label: 'At least 1 number', ok: /[0-9]/.test(value) },
    { id: 'match', label: 'Passwords match', ok: value.length > 0 && value === confirm.value },
  ]
})
const valid = computed(() => checks.value.every((item) => item.ok))

function openWith(nextToken) {
  token = String(nextToken || '').trim()
  password.value = ''
  confirm.value = ''
  open.value = true
  if (!token) {
    state.value = 'invalid'
    copy.value = 'This reset link is missing a token.'
    return
  }
  state.value = 'form'
  copy.value = 'Enter a new password for your account.'
}

async function updatePassword() {
  if (!valid.value) {
    notifyDialog({ type: 'error', message: 'Please meet all password checks before continuing.' })
    return
  }
  if (!token || inFlight) return
  inFlight = true
  state.value = 'saving'
  copy.value = 'Updating your password…'
  try {
    const result = await proxy.$api({
      serviceName: 'session',
      methodName: 'update_password',
      parameters: [token, password.value],
    })
    const message = String(result || '')
    if (message.toLowerCase().includes('have been changed') || message.toLowerCase().includes('has been changed')) {
      state.value = 'success'
      copy.value = 'Your password has been updated. You can sign in with it now.'
      password.value = ''
      confirm.value = ''
      return
    }
    if (message.toLowerCase().includes('does not exist') || message.toLowerCase().includes('invalid')) {
      state.value = 'invalid'
      copy.value = 'This reset link is invalid or has already been used.'
      return
    }
    if (message.toLowerCase().includes('incorrect') || message.toLowerCase().includes('format')) {
      state.value = 'form'
      notifyDialog({ type: 'error', message: 'Password does not meet the required format.' })
      copy.value = 'Enter a new password for your account.'
      return
    }
    state.value = 'error'
    copy.value = message || 'Something went wrong while updating your password.'
  } catch (err) {
    console.error('Password reset failed:', err)
    state.value = 'error'
    copy.value = 'Something went wrong while updating your password. Try again or request a new link.'
  } finally {
    inFlight = false
  }
}

function close() {
  if (state.value !== 'saving') open.value = false
}
function onModel(value) {
  if (value) open.value = true
  else close()
}
function openSignIn() {
  open.value = false
  Driver.showDrawer(true)
}
function requestNewLink() {
  open.value = false
  Driver.showDrawer(true)
}
function retryForm() {
  state.value = 'form'
  copy.value = 'Enter a new password for your account.'
}

watch(
  () => [route.name, route.params.token],
  ([name, nextToken]) => {
    if (name === 'Reset' && nextToken) {
      const value = String(nextToken)
      if (route.path.startsWith('/reset')) router.replace('/')
      openWith(value)
    }
  },
  { immediate: true }
)
</script>

<template><v-dialog :model-value="open" width="560" scrim="rgba(0,0,0,0.72)" transition="dialog-bottom-transition" :persistent="state === 'saving'" @update:model-value="onModel">
    <v-card class="reset-dialog-card">
      <header class="reset-header">
        <div class="reset-header-main">
          <div class="reset-icon" :class="iconClass">
            <v-progress-circular v-if="state === 'saving'" indeterminate size="22" width="2.5" />
            <v-icon v-else size="22">{{ icon }}</v-icon>
          </div>
          <div class="reset-heading">
            <span class="reset-kicker">Account</span>
            <h2>{{ title }}</h2>
          </div>
        </div>
        <button type="button" class="reset-close" :disabled="state === 'saving'" aria-label="Close" @click="close">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </header>
      <p class="reset-copy">{{ copy }}</p>
      <div v-if="state === 'form'" class="reset-form">
        <v-text-field v-model="password" type="password" label="New password" prepend-inner-icon="mdi-lock" variant="outlined" density="comfortable" hide-details="auto" autocomplete="new-password" @keyup.enter="updatePassword" />
        <v-text-field v-model="confirm" type="password" label="Confirm password" prepend-inner-icon="mdi-lock" variant="outlined" density="comfortable" hide-details="auto" class="mt-3" autocomplete="new-password" @keyup.enter="updatePassword" />
        <ul class="pw-checks" aria-label="Password requirements">
          <li v-for="item in checks" :key="item.id" class="pw-check" :class="{ ok: item.ok }">
            <v-icon size="14">{{ item.ok ? 'mdi-check-circle' : 'mdi-radiobox-blank' }}</v-icon>
            <span>{{ item.label }}</span>
          </li>
        </ul>
      </div>
      <div v-if="state !== 'saving'" class="reset-actions">
        <v-btn v-if="state === 'form'" color="primary" variant="flat" :disabled="!valid" @click="updatePassword"> Update password </v-btn>
        <v-btn v-else-if="state === 'success'" color="primary" variant="flat" @click="openSignIn"> Sign in </v-btn>
        <template v-else>
          <v-btn variant="outlined" @click="requestNewLink"> Request new link </v-btn>
          <v-btn v-if="state === 'error'" variant="tonal" @click="retryForm"> Try again </v-btn>
        </template>
      </div>
    </v-card>
  </v-dialog></template>

<style scoped>
.reset-dialog-card {
  overflow:hidden;
  border-radius:16px!important;
  border:1px solid var(--line-strong);
  background:var(--card-bg-solid)!important;
  color:var(--ink);
  padding:20px 20px 18px}
.reset-header {
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:12px}
.reset-header-main {
  display:flex;
  align-items:center;
  gap:12px;
  min-width:0}
.reset-icon {
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:44px;
  height:44px;
  border-radius:12px;
  background:var(--glass-strong);
  color:var(--ink);
  flex-shrink:0}
.reset-icon.success {
  color:var(--status-success, #66bb6a);
  background:color-mix(in srgb,var(--status-success, #66bb6a) 16%,transparent)}
.reset-icon.error, .reset-icon.invalid {
  color:var(--status-danger, #ef5350);
  background:color-mix(in srgb,var(--status-danger, #ef5350) 16%,transparent)}
.reset-heading {
  min-width:0}
.reset-kicker {
  display:block;
  margin-bottom:2px;
  font-size:.68rem;
  font-weight:800;
  letter-spacing:.12em;
  text-transform:uppercase;
  color:var(--mute)}
.reset-heading h2 {
  margin:0;
  font-size:1.12rem;
  font-weight:800;
  letter-spacing:.02em;
  line-height:1.2;
  color:var(--ink)}
.reset-close {
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:36px;
  height:36px;
  border-radius:10px;
  border:1px solid var(--line-strong);
  background:var(--glass);
  color:var(--ink-soft);
  cursor:pointer;
  flex-shrink:0}
.reset-close:disabled {
  opacity:.4;
  cursor:not-allowed}
.reset-close:hover:not(:disabled) {
  background:var(--glass-strong);
  color:var(--ink)}
.reset-copy {
  margin:14px 0 0;
  font-size:.88rem;
  line-height:1.45;
  color:var(--mute);
  text-align:left}
.reset-form {
  margin-top:14px}
.reset-alert {
  margin-bottom:12px!important}
.pw-checks {
  list-style:none;
  margin:12px 0 0;
  padding:0;
  display:grid;
  gap:6px}
.pw-check {
  display:flex;
  align-items:center;
  gap:8px;
  font-size:.8rem;
  font-weight:600;
  color:var(--mute)}
.pw-check .v-icon {
  color:var(--faint);
  flex-shrink:0}
.pw-check.ok, .pw-check.ok .v-icon {
  color:var(--status-success, #66bb6a)}
.reset-actions {
  display:flex;
  flex-wrap:wrap;
  justify-content:flex-end;
  gap:8px;
  margin-top:18px}
</style>
